import React, { useState, useEffect } from "react";
import DataTable from "../components/DataTable.js";
import { fetchDataFromAPI, saveData, deleteData } from "../components/api.js";
import subscriptionStatus from "../components/SubscriptionStatus.js";
import config from "../config";

const baseUrl = config.baseURL;
const getRangeInputProps = (col) => {
  if (col.type === "range") {
    return { min: 0, max: 5, step: 0.5 }; // Limit range inputs to 0-5 with step of 0.1
  }
  return {}; // Return empty object for non-range types
};

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({});
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const isSubscribed = subscriptionStatus();

  useEffect(() => {
    fetchData();
  }, [filters, sortField, sortOrder]);

  const fetchData = async (page = 1, itemsPerPage = 10, filters = {}) => {
    try {
      const token = localStorage.getItem("token");

      const filterParamsObj = {
        page,
        limit: itemsPerPage,
        ...filters,
        includeDeleted: "false",
      };

      if (sortField) filterParamsObj.sortField = sortField;
      if (sortOrder) filterParamsObj.sortOrder = sortOrder;

      const filterParams = new URLSearchParams(filterParamsObj).toString();

      const { data: companies, pagination } = await fetchDataFromAPI(
        `/api/companies`,
        filterParams,
        token
      );
      const processedCompanies = (companies || []).map((company) => ({
        ...company,
        // Assuming company.data_used is already a GB string (e.g., "1500.000")
        // Apply conversion to data_used.
        // If it was "1500.000" (a string), it converts 1500.000 bytes -> ~0.000001 GB
        // If it was 1500 (a number), it converts 1500 bytes -> ~0.000001 GB
        // If it was 1500000000 (bytes), it converts to ~1.4 GB
        // Convert the byte value of data_limit to a GB string for consistent display
        // 1. Subscription Plan ID
        subscription_plan_id:
          company.subscription_plan_id === null
            ? "Not subscribed yet"
            : company.subscription_plan_id,

        // 2. Stripe Customer ID (Used to infer Premium status)
        stripe_customer_id:
          company.stripe_customer_id === null
            ? "Not premium yet"
            : company.stripe_customer_id,

        // 3. Advertiser ID
        advertiser_id:
          company.advertiser_id === null
            ? "Not advertiser yet"
            : company.advertiser_id,
        subscription_expires_at:
          company.subscription_expires_at === null
            ? "Lifetime"
            : company.subscription_expires_at,
        data_limit: bytesToGB(company.data_limit),
        data_used: bytesToGB(company.data_used),
      }));
      setCompanies(processedCompanies || []);
      setTotalPages(pagination?.totalPages || 1);
      console.log("companies", processedCompanies);
    } catch (err) {
      console.error("Error fetching companies:", err.message);
      setError(err.message);
    }
  };

  const handleAddRow = async () => {
    try {
      const newRow = { name: "New Company" };
      if (isOffline) {
        storeOfflineData([newRow], "companies", "add");
      } else {
        await saveData("/api/companies", newRow, "POST");
      }
      fetchData();
    } catch (err) {
      console.error("Error adding company:", err);
    }
  };

  const handleEdit = async (id, updatedData) => {
    try {
      if (isOffline) {
        storeOfflineData([updatedData], "companies", "update");
      } else {
        await saveData(`/api/companies/${id}`, updatedData);
      }
      fetchData();
    } catch (err) {
      console.error("Error editing company:", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      if (isOffline) {
        storeOfflineData([{ id }], "companies", "delete");
      } else {
        await deleteData(`/api/companies/${id}`);
      }
      fetchData();
    } catch (err) {
      console.error("Error deleting company:", err);
    }
  };
  /**
   * Converts bytes to gigabytes and formats the output to 3 decimal places.
   * Handles both number and numeric string inputs (e.g., "1073741824").
   * @param {*} dataValue The value in bytes (number or string).
   * @returns {string} The value in GB, formatted as a string (e.g., "1.000").
   */
  const bytesToGB = (dataValue) => {
    // Try to convert to a number first, handling potential strings like "1500.000"
    const bytes = Number(dataValue);

    if (isNaN(bytes) || bytes <= 0) {
      return "0.000"; // Handle invalid, null, or zero input
    }

    // 1 GB = 1024 * 1024 * 1024 bytes (1,073,741,824 bytes)
    const GB = bytes / 1073741824;

    // If the number is huge (like 1500 * 10^9), the conversion will yield a small number
    // If the number is small (like 1500), the conversion will yield a very tiny number (~0.000001 GB)
    // Since your backend might be inconsistent, we assume any numeric value > 1000 is a byte count.

    // If the API is guaranteed to send bytes when it sends a number, the simple conversion is fine:
    return GB.toFixed(3);
  };
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchData(newPage, 10);
  };

  const handleSort = (field, order) => {
    setSortField(field);
    setSortOrder(order);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchData(currentPage, 10, newFilters);
  };

  // Table columns mapped from SQL schema
  const columns = [
    { header: "Company Name", accessor: "name", type: "showOnlyText" },
    { header: "Logo", accessor: "logo", type: "file" },
    { header: "Type", accessor: "type", type: "showOnlyText" }, // LLC, Corporation
    // { header: "Card Details", accessor: "cardDetails", type: "showOnlyText" }, // ⚠️ careful showing this
    { header: "Website", accessor: "websiteUrl", type: "showOnlyText" },
    { header: "Description", accessor: "description", type: "showOnlyText" },
    { header: "Tagline", accessor: "tagline", type: "showOnlyText" },
    { header: "Size", accessor: "size", type: "showOnlyText" },
    { header: "Currency", accessor: "currency", type: "showOnlyText" },
    { header: "Time Zone", accessor: "timeZone", type: "showOnlyText" },
    { header: "Language", accessor: "language", type: "showOnlyText" },
    { header: "Industry", accessor: "business_industry", type: "showOnlyText" },
    { header: "Category", accessor: "business_category", type: "showOnlyText" },
    // {
    //   header: "Subscription Plan ID",
    //   accessor: "subscription_plan_id",
    //   type: "showOnlyText",
    // },
    { header: "Users", accessor: "user_names", type: "showOnlyText" },
    {
      header: "Stripe Customer ID",
      accessor: "stripe_customer_id",
      type: "showOnlyText",
    },
    {
      header: "Advertiser ID",
      accessor: "advertiser_id",
      type: "showOnlyText",
    },
    { header: "Data Used (GB)", accessor: "data_used", type: "showOnlyRange" },
    {
      header: "Data Limit (GB)",
      accessor: "data_limit",
      type: "showOnlyRange",
    },
    {
      header: "Subscription Type",
      accessor: "subscription_type",
      type: "showOnlyText",
    },
    {
      header: "Subscription Expires",
      accessor: "subscription_expires_at",
      type: "showOnlyDate",
    },
    { header: "Created At", accessor: "createdAt", type: "showOnlyDate" }, // Communication icons
    {
      header: "Email",
      accessor: "email_count",
      type: "icon",
      icon: "email",
      onClick: async (row) => {
        await fetch(`${baseUrl}/api/send-email/${row.id}`, { method: "POST" });
        fetchData(currentPage);
      },
    },
    {
      header: "WhatsApp",
      accessor: "whatsapp_count",
      type: "icon",
      icon: "whatsapp",
      onClick: async (row) => {
        await fetch(`${baseUrl}/api/send-whatsapp/${row.id}`, {
          method: "POST",
        });
        fetchData(currentPage);
      },
    },
    {
      header: "Notification",
      accessor: "notification_count",
      type: "icon",
      icon: "notification",
      onClick: async (row) => {
        await fetch(`${baseUrl}/api/send-notification/${row.id}`, {
          method: "POST",
        });
        fetchData(currentPage);
      },
    },
  ];

  return (
    <DataTable
      title="Companies"
      columns={columns}
      data={companies}
      currentPage={currentPage}
      setData={setCompanies}
      totalPages={totalPages}
      onSort={handleSort}
      sortOrder={sortOrder}
      sortField={sortField}
      onPageChange={handlePageChange}
      filters={filters}
      onFilterChange={handleFilterChange}
      onAddRow={handleAddRow}
      //   actions={[
      //     {
      //       label: "Edit",
      //       onClick: (row) => {
      //         const updatedData = { ...row, name: "Updated Company" };
      //         handleEdit(row.id, updatedData);
      //       },
      //     },
      //     {
      //       label: "Delete",
      //       onClick: (row) => handleDelete(row.id),
      //     },
      //   ]}
      apiEndpoint="/api/companies"
      itemsPerPage={10}
      getRangeInputProps={getRangeInputProps}
      fetchData={fetchData}
      showAddButton={false}
    />
  );
};

export default Companies;
